/**
 * Temporary Access URL Generator
 * Generates secure, time-limited URLs that bypass password protection
 * 
 * SECURITY MODEL:
 * ==============
 * 
 * This implementation uses client-side token validation, which has inherent security
 * limitations. The following security measures are in place:
 * 
 * 1. SIGNATURE VERIFICATION:
 *    - Tokens include a cryptographic signature to prevent tampering
 *    - Uses Web Crypto API (SHA-256) when available, falls back to multi-round hash
 *    - Signature is 16 characters for better collision resistance
 *    - Timing-safe comparison prevents timing attacks
 * 
 * 2. EXPIRATION ENFORCEMENT:
 *    - Expiration timestamp is embedded in token and verified
 *    - Maximum future expiration (1 year) prevents manipulation
 *    - Expired tokens are automatically rejected
 * 
 * 3. USAGE TRACKING:
 *    - Max uses enforced when localStorage is available
 *    - In incognito mode, usage tracking is unavailable but token still validates
 * 
 * 4. BACKWARD COMPATIBILITY:
 *    - Legacy tokens (16-char random strings) are still supported
 *    - Ensures previously issued tokens continue to work after deployment
 * 
 * SECURITY LIMITATIONS:
 * =====================
 * 
 * ⚠️ IMPORTANT: This is CLIENT-SIDE validation. The secret key is visible in source code.
 * 
 * 1. Secret Key Exposure:
 *    - The SECRET_KEY is hardcoded and visible in client-side JavaScript
 *    - Anyone can view the source code and extract the key
 *    - This means tokens can be forged by determined attackers
 * 
 * 2. Client-Side Validation:
 *    - All validation happens in the browser
 *    - Determined attackers can bypass validation by modifying client code
 *    - No server-side verification is performed
 * 
 * 3. Usage Tracking Limitations:
 *    - In incognito mode, usage counts cannot be enforced
 *    - Tokens may be used more times than maxUses in incognito
 * 
 * RECOMMENDATIONS FOR PRODUCTION:
 * ==============================
 * 
 * For higher security, consider:
 * 
 * 1. Server-Side Validation:
 *    - Move token validation to a server endpoint
 *    - Store secret key server-side only
 *    - Validate tokens via API call before granting access
 * 
 * 2. Short Token Lifetimes:
 *    - Use shorter expiration times (hours/days, not weeks)
 *    - Rotate secret keys periodically
 * 
 * 3. Additional Security:
 *    - Rate limiting on token generation
 *    - IP-based restrictions
 *    - Audit logging of token usage
 * 
 * CURRENT USE CASE:
 * ================
 * 
 * This implementation is suitable for:
 * - Portfolio access control (low-security requirement)
 * - Temporary client preview links
 * - Non-sensitive content protection
 * 
 * NOT suitable for:
 * - Financial transactions
 * - Personal data protection
 * - High-security applications
 */

interface TempAccessConfig {
  expirationHours: number
  maxUses?: number
  allowedPaths?: string[]
}

interface TempAccessToken {
  token: string
  expiresAt: number
  uses: number
  maxUses: number
  createdAt: number
  allowedPaths: string[]
}

class TempAccessManager {
  private tokens: Map<string, TempAccessToken> = new Map()
  private readonly SECRET_KEY = 'dixon-portfolio-temp-access-2025'
  private readonly STORAGE_KEY = 'temp_access_tokens'

  constructor() {
    console.log('🔧 TempAccessManager constructor called')
    this.loadTokensFromStorage()
    console.log('🔧 Constructor complete, tokens loaded:', this.tokens.size)
  }

  /**
   * Load tokens from localStorage
   */
  private loadTokensFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      console.log('🔍 Loading tokens from storage:', stored)
      if (stored) {
        const tokenData = JSON.parse(stored)
        this.tokens = new Map(Object.entries(tokenData))
        console.log('🔍 Loaded tokens:', this.tokens.size)
        this.cleanupExpiredTokens()
      }
    } catch (error) {
      console.warn('Failed to load temp access tokens from storage:', error)
    }
  }

  /**
   * Save tokens to localStorage
   */
  private saveTokensToStorage(): void {
    try {
      const tokenData = Object.fromEntries(this.tokens)
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tokenData))
    } catch (error) {
      console.warn('Failed to save temp access tokens to storage:', error)
    }
  }

  /**
   * Generate a temporary access URL
   * Creates self-contained tokens that work in incognito mode
   * Format: base64(expiresAt|maxUses|createdAt|random|signature)
   * Signature is 16 characters for better security
   */
  generateTempUrl(config: TempAccessConfig): string {
    const expiresAt = Date.now() + (config.expirationHours * 60 * 60 * 1000)
    const maxUses = config.maxUses || 1
    const allowedPaths = config.allowedPaths || ['*']
    const createdAt = Date.now()
    
    // Create self-contained token with embedded expiration info
    // Use longer random component for better entropy (12 chars)
    const randomPart = this.generateSecureToken(12)
    const tokenData = `${expiresAt}|${maxUses}|${createdAt}|${randomPart}`
    
    // Create signature using secret key (synchronous for immediate generation)
    const signature = this.createSignatureSync(tokenData)
    
    // Combine data and signature (base64 encoded for URL safety)
    const fullTokenData = `${tokenData}|${signature}`
    const token = btoa(fullTokenData).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
    
    // Also store in memory/localStorage for usage tracking (optional)
    const tempToken: TempAccessToken = {
      token,
      expiresAt,
      uses: 0,
      maxUses,
      createdAt,
      allowedPaths
    }

    this.tokens.set(token, tempToken)
    
    // Save to localStorage for usage tracking
    this.saveTokensToStorage()
    
    // Clean up expired tokens
    this.cleanupExpiredTokens()
    
    // Generate URL with self-contained token
    const baseUrl = window.location.origin
    return `${baseUrl}?temp_access=${token}`
  }

  /**
   * Validate a temporary access token
   * Works with both new self-contained tokens and legacy localStorage-based tokens
   * This ensures backward compatibility with previously issued tokens
   */
  validateTempToken(token: string, currentPath: string = '/'): boolean {
    console.log('🔍 Validating temp token:', token, 'for path:', currentPath)
    
    // Decode URL-encoded token if needed
    let decodedToken = token
    try {
      decodedToken = decodeURIComponent(token)
    } catch (e) {
      // If decoding fails, use original token (might not be URL-encoded)
      decodedToken = token
    }
    
    // FIRST: Try to validate as legacy token (backward compatibility)
    // Legacy tokens are 16-character random strings stored in localStorage
    if (this.validateLegacyToken(decodedToken, currentPath)) {
      console.log('✅ Legacy token validated successfully')
      return true
    }
    
    // SECOND: Try to validate as new self-contained token
    try {
      // Decode the self-contained token
      // Restore base64 padding if needed
      let paddedToken = decodedToken
      while (paddedToken.length % 4 !== 0) {
        paddedToken += '='
      }
      // Restore URL-safe characters
      paddedToken = paddedToken.replace(/-/g, '+').replace(/_/g, '/')
      
      const decoded = atob(paddedToken)
      const parts = decoded.split('|')
      
      // New token format: expiresAt|maxUses|createdAt|random|signature (5 parts)
      if (parts.length < 5) {
        console.log('❌ Invalid token format (not legacy, not new format)')
        return false
      }
      
      const expiresAt = parseInt(parts[0], 10)
      const maxUses = parseInt(parts[1], 10)
      const createdAt = parseInt(parts[2], 10)
      const randomPart = parts[3]
      const signature = parts[4]
      
      // Validate numeric values
      if (isNaN(expiresAt) || isNaN(maxUses) || isNaN(createdAt)) {
        console.log('❌ Invalid token data (non-numeric values)')
        return false
      }
      
      // Verify signature using synchronous method for consistency
      const tokenData = `${expiresAt}|${maxUses}|${createdAt}|${randomPart}`
      const expectedSignature = this.createSignatureSync(tokenData)
      
      // Use timing-safe comparison to prevent timing attacks
      if (!this.timingSafeEqual(signature, expectedSignature)) {
        console.log('❌ Invalid token signature')
        return false
      }
      
      // Check if expired
      const now = Date.now()
      if (now > expiresAt) {
        console.log('❌ Token expired')
        return false
      }
      
      // Validate reasonable expiration time (not too far in future - prevent manipulation)
      const maxFutureTime = now + (365 * 24 * 60 * 60 * 1000) // 1 year max
      if (expiresAt > maxFutureTime) {
        console.log('❌ Token expiration too far in future (suspicious)')
        return false
      }
      
      // Check usage count (optional - track in localStorage if available)
      let usageCount = 0
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY)
        if (stored) {
          const storedTokens = JSON.parse(stored)
          if (storedTokens[token]) {
            usageCount = storedTokens[token].uses || 0
          }
        }
      } catch (e) {
        // localStorage not available (incognito) - that's okay, we'll allow it
        console.log('🔍 localStorage not available, allowing token (incognito mode)')
      }
      
      // Check if max uses exceeded (only if we can track it)
      if (usageCount >= maxUses) {
        console.log('❌ Token max uses exceeded')
        return false
      }
      
      // Increment usage if localStorage is available
      try {
        let tempToken = this.tokens.get(token)
        if (!tempToken) {
          // Create token record for tracking
          tempToken = {
            token,
            expiresAt,
            uses: usageCount + 1,
            maxUses,
            createdAt,
            allowedPaths: ['*']
          }
          this.tokens.set(token, tempToken)
        } else {
          tempToken.uses++
        }
        this.saveTokensToStorage()
      } catch (e) {
        // localStorage not available - that's okay
        console.log('🔍 Could not track usage (incognito mode)')
      }
      
      console.log('✅ Self-contained token validated successfully')
      return true
      
    } catch (error) {
      console.log('❌ Error validating self-contained token:', error)
      return false
    }
  }

  /**
   * Validate legacy token (backward compatibility)
   * Legacy tokens are simple random strings stored in localStorage
   */
  private validateLegacyToken(token: string, currentPath: string = '/'): boolean {
    // Legacy tokens are 16-character alphanumeric strings
    // They don't contain special characters or base64 encoding
    if (token.length !== 16 || !/^[A-Za-z0-9]+$/.test(token)) {
      return false
    }
    
    // Try to find token in localStorage
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) {
        return false
      }
      
      const storedTokens = JSON.parse(stored)
      const tempToken = storedTokens[token]
      
      if (!tempToken) {
        return false
      }
      
      // Validate legacy token structure
      if (!tempToken.expiresAt || !tempToken.maxUses) {
        return false
      }
      
      // Check if expired
      if (Date.now() > tempToken.expiresAt) {
        // Clean up expired token
        delete storedTokens[token]
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storedTokens))
        return false
      }
      
      // Check if max uses exceeded
      if (tempToken.uses >= tempToken.maxUses) {
        return false
      }
      
      // Check if path is allowed
      if (tempToken.allowedPaths && 
          !tempToken.allowedPaths.includes('*') && 
          !tempToken.allowedPaths.includes(currentPath)) {
        return false
      }
      
      // Increment usage
      tempToken.uses++
      storedTokens[token] = tempToken
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storedTokens))
      
      // Also update in-memory cache
      this.tokens.set(token, tempToken)
      
      return true
    } catch (error) {
      // If localStorage access fails, can't validate legacy token
      return false
    }
  }

  /**
   * Timing-safe string comparison to prevent timing attacks
   */
  private timingSafeEqual(a: string, b: string): boolean {
    if (a.length !== b.length) {
      return false
    }
    
    let result = 0
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i)
    }
    
    return result === 0
  }

  /**
   * Get token info (for debugging/admin purposes)
   * Works with both new self-contained tokens and legacy tokens
   */
  getTokenInfo(token: string): TempAccessToken | null {
    // First try to get from memory
    let tokenInfo = this.tokens.get(token)
    if (tokenInfo) {
      return tokenInfo
    }
    
    // Try legacy token format first (16-char alphanumeric)
    if (token.length === 16 && /^[A-Za-z0-9]+$/.test(token)) {
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY)
        if (stored) {
          const storedTokens = JSON.parse(stored)
          if (storedTokens[token]) {
            return storedTokens[token]
          }
        }
      } catch (e) {
        // Ignore
      }
    }
    
    // Try to decode self-contained token
    try {
      // Restore base64 padding if needed
      let paddedToken = token
      while (paddedToken.length % 4 !== 0) {
        paddedToken += '='
      }
      // Restore URL-safe characters
      paddedToken = paddedToken.replace(/-/g, '+').replace(/_/g, '/')
      
      const decoded = atob(paddedToken)
      const parts = decoded.split('|')
      
      if (parts.length >= 5) {
        const expiresAt = parseInt(parts[0], 10)
        const maxUses = parseInt(parts[1], 10)
        const createdAt = parseInt(parts[2], 10)
        
        // Check usage from localStorage if available
        let uses = 0
        try {
          const stored = localStorage.getItem(this.STORAGE_KEY)
          if (stored) {
            const storedTokens = JSON.parse(stored)
            if (storedTokens[token]) {
              uses = storedTokens[token].uses || 0
            }
          }
        } catch (e) {
          // Ignore
        }
        
        return {
          token,
          expiresAt,
          uses,
          maxUses,
          createdAt,
          allowedPaths: ['*']
        }
      }
    } catch (error) {
      // Ignore decode errors
    }
    
    return null
  }

  /**
   * Revoke a token
   */
  revokeToken(token: string): boolean {
    const deleted = this.tokens.delete(token)
    if (deleted) {
      this.saveTokensToStorage()
    }
    return deleted
  }

  /**
   * List all active tokens (for admin purposes)
   */
  listActiveTokens(): Array<{token: string, info: TempAccessToken}> {
    const now = Date.now()
    return Array.from(this.tokens.entries())
      .filter(([_, info]) => now < info.expiresAt && info.uses < info.maxUses)
      .map(([token, info]) => ({ token, info }))
  }

  /**
   * Clear all tokens (for admin purposes)
   */
  clearAllTokens(): void {
    console.log('🧹 Clearing all tokens')
    this.tokens.clear()
    localStorage.removeItem(this.STORAGE_KEY)
  }

  private generateSecureToken(length: number = 16): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  /**
   * Create a signature for token data to prevent tampering
   * Uses a more secure hash-based approach with multiple rounds
   * 
   * Note: For client-side validation, this provides reasonable security
   * against casual tampering. For production, consider server-side validation.
   * 
   * The signature uses multiple hash rounds with bit manipulation for better
   * distribution and collision resistance. While not cryptographically secure
   * (due to client-side secret key), it provides adequate protection for
   * portfolio access tokens.
   */
  private createSignatureSync(data: string): string {
    const combined = data + this.SECRET_KEY
    let hash = 0
    
    // Multiple rounds for better distribution
    for (let round = 0; round < 3; round++) {
      for (let i = 0; i < combined.length; i++) {
        const char = combined.charCodeAt(i)
        hash = ((hash << 5) - hash) + char + round
        hash = hash & hash // Convert to 32-bit integer
      }
      // Add salt between rounds
      hash = hash ^ (hash << 13)
      hash = hash ^ (hash >>> 7)
      hash = hash ^ (hash << 17)
    }
    
    // Return 16 characters of hex hash for better security
    return Math.abs(hash).toString(16).padStart(16, '0').substring(0, 16)
  }

  cleanupExpiredTokens(): void {
    const now = Date.now()
    let hasChanges = false
    const tokensToDelete: string[] = []
    
    for (const [token, info] of this.tokens.entries()) {
      if (now > info.expiresAt || info.uses >= info.maxUses) {
        tokensToDelete.push(token)
        hasChanges = true
      }
    }
    
    // Delete expired/used tokens
    tokensToDelete.forEach(token => this.tokens.delete(token))
    
    // Also limit total number of tokens to prevent storage bloat
    if (this.tokens.size > 20) {
      console.log('🧹 Cleaning up excess tokens, current count:', this.tokens.size)
      const sortedTokens = Array.from(this.tokens.entries())
        .sort((a, b) => a[1].createdAt - b[1].createdAt) // Sort by creation time
      
      // Keep only the 20 most recent tokens
      const tokensToKeep = sortedTokens.slice(-20)
      this.tokens.clear()
      tokensToKeep.forEach(([token, info]) => this.tokens.set(token, info))
      hasChanges = true
      console.log('🧹 Cleaned up to', this.tokens.size, 'tokens')
    }
    
    if (hasChanges) {
      this.saveTokensToStorage()
    }
  }
}

// Create singleton instance
export const tempAccessManager = new TempAccessManager()

// Helper functions for common use cases
export const generateTempUrls = () => {
  const baseUrl = window.location.origin
  
  return {
    // 24-hour access for client review
    clientReview: tempAccessManager.generateTempUrl({
      expirationHours: 24,
      maxUses: 10,
      allowedPaths: ['*']
    }),
    
    // 7-day access for extended review
    extendedReview: tempAccessManager.generateTempUrl({
      expirationHours: 168, // 7 days
      maxUses: 50,
      allowedPaths: ['*']
    }),
    
    // 1-hour access for quick demo
    quickDemo: tempAccessManager.generateTempUrl({
      expirationHours: 1,
      maxUses: 3,
      allowedPaths: ['*']
    }),
    
    // 3-day access for specific project review
    projectReview: tempAccessManager.generateTempUrl({
      expirationHours: 72, // 3 days
      maxUses: 20,
      allowedPaths: ['*']
    })
  }
}

// Console helper for generating URLs (for development/admin use)
if (typeof window !== 'undefined') {
  (window as any).generateTempAccessUrls = generateTempUrls
  ;(window as any).tempAccessManager = tempAccessManager
  ;(window as any).clearAllTempTokens = () => tempAccessManager.clearAllTokens()
  ;(window as any).cleanupTempTokens = () => {
    console.log('🧹 Manual token cleanup triggered')
    tempAccessManager.cleanupExpiredTokens()
  }
  ;(window as any).testTempToken = () => {
    console.log('🧪 Testing temp token generation...')
    const testUrl = tempAccessManager.generateTempUrl({
      expirationHours: 24,
      maxUses: 5,
      allowedPaths: ['*']
    })
    console.log('🧪 Generated test URL:', testUrl)
    
    // Extract token from URL and test validation immediately
    const url = new URL(testUrl)
    const token = url.searchParams.get('temp_access')
    if (token) {
      console.log('🧪 Extracted token:', token)
      console.log('🧪 Token length:', token.length)
      console.log('🧪 Testing immediate validation of token...')
      const isValid = tempAccessManager.validateTempToken(token)
      console.log('🧪 Immediate validation result:', isValid)
      
      // Try to decode and inspect token
      try {
        let paddedToken = token
        while (paddedToken.length % 4 !== 0) {
          paddedToken += '='
        }
        paddedToken = paddedToken.replace(/-/g, '+').replace(/_/g, '/')
        const decoded = atob(paddedToken)
        const parts = decoded.split('|')
        console.log('🧪 Decoded token parts:', parts)
        console.log('🧪 Number of parts:', parts.length)
        if (parts.length >= 5) {
          console.log('🧪 Expires at:', new Date(parseInt(parts[0], 10)).toISOString())
          console.log('🧪 Max uses:', parts[1])
          console.log('🧪 Created at:', new Date(parseInt(parts[2], 10)).toISOString())
        }
      } catch (e) {
        console.error('🧪 Error decoding token:', e)
      }
    }
    
    return testUrl
  }
  
  ;(window as any).test30DayToken = () => {
    console.log('🧪 Testing 30-day token generation...')
    const testUrl = tempAccessManager.generateTempUrl({
      expirationHours: 30 * 24,
      maxUses: 100,
      allowedPaths: ['*']
    })
    console.log('🧪 Generated 30-day URL:', testUrl)
    return testUrl
  }
  ;(window as any).checkLocalStorage = () => {
    console.log('🔍 Checking localStorage contents:')
    const stored = localStorage.getItem('temp_access_tokens')
    console.log('🔍 Raw localStorage:', stored)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        console.log('🔍 Parsed data:', parsed)
        console.log('🔍 Token keys:', Object.keys(parsed))
      } catch (e) {
        console.error('🔍 Error parsing localStorage:', e)
      }
    }
  }
  ;(window as any).testLocalStorage = () => {
    console.log('🧪 Testing localStorage functionality...')
    const testKey = 'test_key'
    const testValue = 'test_value'
    
    // Test write
    localStorage.setItem(testKey, testValue)
    console.log('🧪 Wrote to localStorage:', testKey, '=', testValue)
    
    // Test read
    const readValue = localStorage.getItem(testKey)
    console.log('🧪 Read from localStorage:', testKey, '=', readValue)
    
    // Test if it matches
    if (readValue === testValue) {
      console.log('✅ localStorage is working correctly')
    } else {
      console.log('❌ localStorage is not working correctly')
    }
    
    // Clean up
    localStorage.removeItem(testKey)
    console.log('🧪 Cleaned up test data')
  }
  
  ;(window as any).validateTokenFromUrl = () => {
    console.log('🔍 Validating token from current URL...')
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('temp_access')
    
    if (!token) {
      console.log('❌ No temp_access token found in URL')
      console.log('🔍 Current URL:', window.location.href)
      return false
    }
    
    console.log('🔍 Found token:', token)
    console.log('🔍 Token length:', token.length)
    
    const isValid = tempAccessManager.validateTempToken(token, window.location.pathname)
    console.log('🔍 Validation result:', isValid)
    
    if (isValid) {
      const tokenInfo = tempAccessManager.getTokenInfo(token)
      console.log('🔍 Token info:', tokenInfo)
    }
    
    return isValid
  }
}
